/**
 * Excel Export Utilities
 *
 * Simple Excel XML format export (no external dependencies).
 * Produces XML Spreadsheet 2003 format which opens in Excel.
 */

import { resolveCellValue } from '../../core/internal/value-accessor';
import type { ColumnConfig } from '../../core/types';
import { downloadBlob } from './csv';
import { buildColumnWidthsXml, buildStyleRegistry, resolveDataStyleId } from './excel-styles';
import type { ExportParams } from './types';

/**
 * Escape XML special characters.
 */
function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Build Excel XML content from rows and columns.
 * Uses XML Spreadsheet 2003 format for broad compatibility.
 */
export function buildExcelXml(rows: any[], columns: ColumnConfig[], params: ExportParams): string {
  const styles = params.excelStyles;
  const registry = styles ? buildStyleRegistry(styles) : undefined;

  let xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">`;

  // Emit <Styles> block (only when styles are configured)
  if (registry) {
    // Pre-register dynamic cellStyle entries so they appear in <Styles>
    if (styles!.cellStyle) {
      for (const row of rows) {
        for (const col of columns) {
          const value = resolveCellValue(row, col);
          const dynamic = styles!.cellStyle(value, col.field, row);
          if (dynamic) registry.register(dynamic);
        }
      }
    }
    xml += registry.toXml();
  }

  xml += '\n<Worksheet ss:Name="Sheet1">\n<Table>';

  // Column widths
  if (styles) {
    xml += buildColumnWidthsXml(columns, rows as Record<string, unknown>[], styles);
  }

  // Header style ID
  const headerStyleId = styles?.headerStyle && registry ? registry.getStyleId(styles.headerStyle) : undefined;
  // Group-header style ID (for plugin-contributed rows). Falls back to
  // headerStyleId so users get sensible defaults without configuring twice.
  const groupHeaderStyleId =
    styles?.groupHeaderStyle && registry ? registry.getStyleId(styles.groupHeaderStyle) : headerStyleId;

  // Build plugin-contributed header rows (e.g. column groups) above the leaf
  // header. Each row's cells must independently span the full column count;
  // `span > 1` becomes `ss:MergeAcross="span-1"` and we skip the next
  // `span-1` cell slots (Excel implicitly fills them from the merge).
  if (params.includeHeaders !== false && params.headerRows && params.headerRows.length > 0) {
    const styleAttr = groupHeaderStyleId ? ` ss:StyleID="${groupHeaderStyleId}"` : '';
    for (const headerRow of params.headerRows) {
      xml += '\n<Row>';
      for (const cell of headerRow.cells) {
        const span = Math.max(1, cell.span | 0);
        const mergeAttr = span > 1 ? ` ss:MergeAcross="${span - 1}"` : '';
        xml += `<Cell${styleAttr}${mergeAttr}><Data ss:Type="String">${escapeXml(cell.label ?? '')}</Data></Cell>`;
      }
      xml += '</Row>';
    }
  }

  // Build header row
  if (params.includeHeaders !== false) {
    xml += '\n<Row>';
    for (const col of columns) {
      const header = col.header || col.field;
      const processed = params.processHeader ? params.processHeader(header, col.field) : header;
      const styleAttr = headerStyleId ? ` ss:StyleID="${headerStyleId}"` : '';
      xml += `<Cell${styleAttr}><Data ss:Type="String">${escapeXml(processed)}</Data></Cell>`;
    }
    xml += '</Row>';
  }

  // Build data rows
  for (const row of rows) {
    xml += '\n<Row>';
    for (const col of columns) {
      let value = resolveCellValue(row, col);
      if (params.processCell) {
        value = params.processCell(value, col.field, row);
      }

      // Determine cell type based on value
      let type: 'Number' | 'String' | 'DateTime' = 'String';
      let displayValue = '';

      if (value == null) {
        displayValue = '';
      } else if (typeof value === 'number' && !isNaN(value)) {
        type = 'Number';
        displayValue = String(value);
      } else if (value instanceof Date) {
        type = 'DateTime';
        displayValue = value.toISOString();
      } else {
        displayValue = escapeXml(String(value));
      }

      // Resolve data cell style
      const dataStyleId = registry && styles ? resolveDataStyleId(registry, styles, value, col.field, row) : undefined;
      const styleAttr = dataStyleId ? ` ss:StyleID="${dataStyleId}"` : '';

      xml += `<Cell${styleAttr}><Data ss:Type="${type}">${displayValue}</Data></Cell>`;
    }
    xml += '</Row>';
  }

  xml += '\n</Table>\n</Worksheet>\n</Workbook>';
  return xml;
}

/**
 * Download Excel XML content as a file.
 */
export function downloadExcel(content: string, fileName: string): void {
  const blob = new Blob([content], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  });
  downloadBlob(blob, fileName);
}
